import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Campanha } from '@eleg360/types';
import { campanhasApi } from '@/lib/api/campanhas';
import { contatosApi } from '@/lib/api/contatos';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { formatarData, formatarTelefone } from '@/lib/format';
import { Drawer } from '@/design-system/Drawer';
import { descreverPublico } from './dados';
import { WizardCampanha } from './WizardCampanha';
import { Info } from '@/design-system/Info';

const TAGS = ['Saúde', 'Iluminação', 'Creche', 'Liderança', 'Comércio', 'Juventude', 'Educação'];

const CHIP_STATUS: Record<string, string> = {
  aprovada: 'good',
  aguardando_aprovacao: 'warn',
  rascunho: 'neutral',
  cancelada: 'crit',
};

const rotuloStatus = (s: string) =>
  s === 'aguardando_aprovacao' ? 'aguardando aprovação' : s;

export function CampanhasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const usuario = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  /**
   * Continuação do motor de convite da agenda (proto:1281): acima do limiar o
   * protótipo grava `STATE.camp.terr` e `STATE.camp.msg` e só então navega
   * para cá. Sem consumir isso, quem vinha da agenda encontrava a tela em
   * branco e refazia território e mensagem do zero.
   */
  const { state } = useLocation();
  const vindoDoConvite = (state as any)?.origem === 'agenda-convite' ? (state as any) : null;

  const [aba, setAba] = useState<'lista' | 'compor' | 'caixa' | 'estrategicas'>(
    vindoDoConvite ? 'compor' : 'lista',
  );
  const [selecionada, setSelecionada] = useState<Campanha | null>(null);
  const [wizardKey, setWizardKey] = useState(0);

  // Compositor
  const [nome, setNome] = useState(
    vindoDoConvite?.compromisso ? `Convite: ${vindoDoConvite.compromisso}` : '',
  );
  const [bairro, setBairro] = useState(vindoDoConvite?.bairro ?? '');
  const [tag, setTag] = useState('');
  const [mensagem, setMensagem] = useState(
    vindoDoConvite?.mensagem ??
      'Olá! Aqui é do gabinete da vereadora Ana Silva. Passando para prestar contas das ações no nosso bairro. Para não receber mais estas mensagens, envie SAIR.',
  );

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: campanhas = [], isLoading } = useQuery({
    queryKey: ['campanhas'],
    queryFn: () => campanhasApi.list(token),
    enabled: !!token,
  });

  const { data: inbox = [] } = useQuery({
    queryKey: ['campanhas-inbox'],
    queryFn: () => campanhasApi.getInbox(token),
    enabled: !!token,
  });

  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });

  // Prévia do público: os mesmos filtros que o servidor aplicará
  const { data: previaPagina } = useQuery({
    queryKey: ['contatos', 'previa-campanha', bairro, tag],
    queryFn: () =>
      contatosApi.list(
        { bairro: bairro || undefined, tag: tag || undefined, base_legal: 'optin', limit: 1 },
        token,
      ),
    enabled: !!token && aba === 'compor',
  });
  const totalPublico = previaPagina?.total ?? 0;

  const limiar = mandato?.configuracoes?.limiar_campanha ?? 100;
  const precisaAprovacao = totalPublico > limiar;

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['campanhas'] });
    queryClient.invalidateQueries({ queryKey: ['outbox'] });
  };

  const criar = useMutation({
    mutationFn: (dados: any) => campanhasApi.create(dados, token),
    onSuccess: (c) => {
      invalidar();
      toast(
        c.status === 'aprovada'
          ? 'Campanha criada e enfileirada para disparo.'
          : 'Campanha criada e enviada para aprovação do titular.',
      );
      setAba('lista');
      setNome('');
    },
    // O servidor recusa público vazio. O bloqueio de período eleitoral existe,
    // mas depende da chave manual BLOQUEIO_PERIODO_ELEITORAL — não há checagem
    // de calendário: conferir a data é responsabilidade do gabinete.
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar a campanha.')),
  });

  const aprovar = useMutation({
    mutationFn: (id: string) => campanhasApi.approve(id, token),
    onSuccess: () => {
      invalidar();
      toast('Campanha aprovada e enfileirada para disparo.');
      setSelecionada(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao aprovar a campanha.')),
  });

  const podeAprovar = usuario?.perfil === 'titular' || usuario?.perfil === 'admin';
  const pendentes = campanhas.filter((c) => c.status === 'aguardando_aprovacao');
  const naoLidas = inbox.filter((m) => !m.lida).length;
  const estrategicas = campanhas.filter((c) => c.tipo === 'estrategica');

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast('Dê um nome à campanha.');
      return;
    }
    if (!/\bSAIR\b/i.test(mensagem)) {
      toast('A mensagem precisa informar como o contato pode sair (ex: "envie SAIR").');
      return;
    }
    if (totalPublico === 0) {
      toast('Nenhum contato com opt-in no público filtrado.');
      return;
    }
    criar.mutate({
      nome: nome.trim(),
      tipo: 'comunicacao',
      publico_def: { bairro: bairro || undefined, tag: tag || undefined },
      mensagem_texto: mensagem.trim(),
    });
  };

  return (
    <div className="page" id="p-campanhas">
      <div className="head">
        <div>
          <h1>Campanhas</h1>
          <p>
            {/*
              proto:1636 chamava as perguntas de "sugeridas pela IA" e dizia
              que "o resultado vira fonte para o território e para o OTO".
              Nenhum dos dois: `sugerirPerguntas()` escolhe um tema por palavra
              chave e devolve um banco fixo (`dados.ts`), e nada transforma o
              resultado em `oto.fonte` — o tipo `estrategica` é só um valor da
              coluna `tipo` de `gab.campanha`.

              proto:628 falava em "três camadas de base legal". Camadas
              diferentes não existem: existe UMA condição
              (`base_legal === 'optin'`) reconferida em três momentos
              independentes — `calcularPublicoAlvo`, a recomputação do público
              em `approve()` e a revalidação do worker no envio. "E-mail"
              também saiu: não há envio de e-mail na API.
            */}
            {aba === 'estrategicas'
              ? 'Campanhas estratégicas: questionário montado a partir de um banco de perguntas por tema, escalas de concordância e leitura cruzada das respostas por território. A leitura fica nesta tela: o resultado não volta como fonte para o OTO.'
              : 'Disparos para a base com opt-in, conferido três vezes: ao montar o público, na aprovação e no instante do envio. Acima do limiar configurado, o envio exige aprovação explícita do titular.'}
          </p>
        </div>
        {aba === 'estrategicas' && (
          <div className="act">
            <button
              type="button"
              className="btn"
              onClick={() => setWizardKey((k) => k + 1)}
            >
              Nova campanha estratégica
            </button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Campanhas</div>
          <div className="v">{campanhas.length}</div>
          <div className="d">{campanhas.filter((c) => c.status === 'aprovada').length} aprovadas</div>
        </div>
        <div className="card kpi">
          <div className="l">Aguardando aprovação</div>
          <div className="v" style={{ color: pendentes.length ? 'var(--warn)' : 'var(--ink)' }}>
            {pendentes.length}
          </div>
          <div className="d">acima de {limiar} destinatários</div>
        </div>
        <div className="card kpi">
          <div className="l">Respostas recebidas</div>
          <div className="v">{inbox.length}</div>
          <div className="d">{naoLidas} não lida(s)</div>
        </div>
        <div className="card kpi">
          <div className="l">Limiar de aprovação</div>
          <div className="v">{limiar}</div>
          <div className="d">definido em Admin › Limiares</div>
        </div>
      </div>

      {/* Abas */}
      <div className="tabs">
        <button className={aba === 'lista' ? 'on' : ''} onClick={() => setAba('lista')}>
          Campanhas
        </button>
        <button className={aba === 'compor' ? 'on' : ''} onClick={() => setAba('compor')}>
          Compor
        </button>
        <button className={aba === 'caixa' ? 'on' : ''} onClick={() => setAba('caixa')}>
          Caixa de entrada {naoLidas > 0 && `(${naoLidas})`}
        </button>
        <button className={aba === 'estrategicas' ? 'on' : ''} onClick={() => setAba('estrategicas')}>
          Campanhas estratégicas <span className="cnt">{estrategicas.length}</span>
        </button>
      </div>

      {/* Lista */}
      {aba === 'lista' && (
        <div className="card flat tw">
          <table>
            <thead>
              <tr>
                <th>Campanha</th>
                <th>Público</th>
                <th style={{ textAlign: 'right' }}>Enviadas</th>
                <th style={{ textAlign: 'right' }}>Respostas</th>
                <th>Criada em</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {campanhas.map((c) => (
                <tr key={c.id} className="row" style={{ cursor: 'pointer' }} onClick={() => setSelecionada(c)}>
                  <td>
                    <b>{c.nome}</b>
                    <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{c.tipo}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {descreverPublico(c.publico_def)}
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {c.enviadas}
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {c.respostas}
                  </td>
                  <td>{formatarData(c.created_at)}</td>
                  <td>
                    <span className={`chip ${CHIP_STATUS[c.status] || 'neutral'}`}>
                      {rotuloStatus(c.status)}
                    </span>
                  </td>
                </tr>
              ))}

              {isLoading && (
                <tr>
                  <td colSpan={6} className="empty">
                    Carregando campanhas...
                  </td>
                </tr>
              )}
              {!isLoading && campanhas.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty">
                    Nenhuma campanha criada. Use a aba <b>Compor</b>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Compor */}
      {aba === 'compor' && (
        <div className="grid g21" style={{ marginTop: 12 }}>
          <form className="card" onSubmit={enviar}>
            <h2>Nova campanha</h2>

            {/*
              Fica visível: quem chega da agenda supõe que vai disparar para os
              convidados que escolheu, e o público desta campanha é outro.
            */}
            {vindoDoConvite && (
              <div className="note warn" style={{ marginTop: 8, fontSize: 12 }} data-testid="origem-convite">
                <b>Continuação do convite de "{vindoDoConvite.compromisso}".</b> O público real é o
                recorte inteiro com opt-in, não a seleção nominal feita na agenda.{' '}
                <Info titulo="O que veio do convite">
                  Os {vindoDoConvite.destinatarios} convidados já ficaram registrados no
                  compromisso; o disparo em massa é que depende desta campanha. Território e
                  mensagem vieram de lá: confira antes de enviar.
                </Info>
              </div>
            )}

            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="cp-nome">Nome da campanha *</label>
              <input
                id="cp-nome"
                className="in"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Informativo ações de saúde"
                required
              />
            </div>

            <div className="grid g2">
              <div className="field">
                <label htmlFor="cp-territorio">Território</label>
                <select id="cp-territorio" className="in" value={bairro} onChange={(e) => setBairro(e.target.value)}>
                  <option value="">Todos</option>
                  {bairros.map((b) => (
                    <option key={b.id} value={b.n}>
                      {b.n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="cp-tag">Tag de interesse</label>
                <select id="cp-tag" className="in" value={tag} onChange={(e) => setTag(e.target.value)}>
                  <option value="">Todas</option>
                  {TAGS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="cp-mensagem">Mensagem *</label>
              <textarea
                id="cp-mensagem"
                className="in"
                style={{ minHeight: 130 }}
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                required
              />
              <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                Este é o texto que será efetivamente enviado. Precisa conter a instrução de
                descadastro.
              </small>
            </div>

            <button type="submit" className="btn pri" disabled={criar.isPending}>
              {criar.isPending
                ? 'Enviando...'
                : precisaAprovacao
                ? 'Enviar para aprovação'
                : 'Criar e disparar'}
            </button>
          </form>

          {/* Prévia */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="card kpi">
              <div className="l">
                Destinatários elegíveis{' '}
                <Info titulo="Quem entra nesta conta">
                  Só contatos com opt-in ativo. Quem está sem base legal ou pediu descadastro nunca
                  é incluído, independentemente do filtro.
                </Info>
              </div>
              <div className="v" style={{ color: totalPublico ? 'var(--good)' : 'var(--crit)' }}>
                {totalPublico}
              </div>
              <div className="d">contatos com opt-in no recorte</div>
            </div>

            {precisaAprovacao && (
              <div className="note warn" style={{ fontSize: 12 }}>
                <b>Acima do limiar de {limiar}:</b> a campanha fica aguardando aprovação do titular
                antes de qualquer disparo.
              </div>
            )}

            <div className="card">
              <h3 style={{ fontSize: 13, margin: 0 }}>Prévia</h3>
              <div className="wa" style={{ marginTop: 10 }}>
                <div className="bub" style={{ fontSize: 12 }}>
                  {mensagem}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Caixa de entrada */}
      {aba === 'caixa' && (
        <div className="card flat tw">
          <table>
            <thead>
              <tr>
                <th>Remetente</th>
                <th>Mensagem</th>
                <th>Recebida em</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {inbox.map((m) => (
                <tr key={m.id} className="row">
                  <td>
                    <b>{m.remetente_nome || 'Não identificado'}</b>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      {formatarTelefone(m.remetente_telefone)}
                    </div>
                  </td>
                  <td style={{ fontSize: 13, maxWidth: 420 }}>{m.mensagem_texto}</td>
                  <td style={{ fontSize: 12 }}>{formatarData(m.received_at)}</td>
                  <td>
                    <span className={`chip ${m.lida ? 'neutral' : 'warn'}`}>
                      {m.lida ? 'lida' : 'não lida'}
                    </span>
                  </td>
                </tr>
              ))}
              {inbox.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    Nenhuma resposta recebida. A caixa se popula quando a integração com o WhatsApp
                    estiver conectada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Campanhas estratégicas */}
      {aba === 'estrategicas' && (
        <div style={{ marginTop: 12 }}>
          {estrategicas.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <h2>
                Campanhas estratégicas anteriores{' '}
                <Info titulo="O que esta lista guarda de cada campanha">
                  A leitura detalhada (perguntas, escalas e cruzamento) fica disponível apenas na
                  sessão em que a campanha foi criada. Persistir esse resultado estruturado é uma
                  tarefa própria, ainda não implementada.
                </Info>
              </h2>
              {estrategicas.map((c) => (
                <div
                  key={c.id}
                  className="n"
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--line)',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelecionada(c)}
                >
                  <b>{c.nome}</b>
                  <br />
                  <span style={{ color: 'var(--ink3)', fontSize: 12 }}>
                    {formatarData(c.created_at)} · {rotuloStatus(c.status)}
                  </span>
                </div>
              ))}

            </div>
          )}
          <WizardCampanha
            key={wizardKey}
            aoConcluir={() => queryClient.invalidateQueries({ queryKey: ['campanhas'] })}
          />
        </div>
      )}

      {/* Drawer: detalhe */}
      {selecionada && (
        <Drawer
          aberto
          titulo={selecionada.nome}
          aoFechar={() => setSelecionada(null)}
        >
          <div className="filters" style={{ marginBottom: 12 }}>
            <span className={`chip ${CHIP_STATUS[selecionada.status] || 'neutral'}`}>
              {rotuloStatus(selecionada.status)}
            </span>
            <span className="chip neutral">{selecionada.tipo}</span>
          </div>

          <dl className="dl">
            <dt>Público definido</dt>
            <dd>
              {descreverPublico(selecionada.publico_def)}
            </dd>
            <dt>Enviadas</dt>
            <dd className="num">{selecionada.enviadas}</dd>
            <dt>Entregues</dt>
            <dd className="num">{selecionada.entregues}</dd>
            <dt>Respostas</dt>
            <dd className="num">{selecionada.respostas}</dd>
            <dt>Criada em</dt>
            <dd>{formatarData(selecionada.created_at)}</dd>
          </dl>

          {selecionada.mensagem_texto && (
            <>
              <h3 style={{ marginTop: 14 }}>Mensagem</h3>
              <div className="wa" style={{ marginTop: 8 }}>
                <div className="bub" style={{ fontSize: 12 }}>
                  {selecionada.mensagem_texto}
                </div>
              </div>
            </>
          )}

          {selecionada.status === 'aguardando_aprovacao' && (
            <>
              <div className="note warn" style={{ marginTop: 14, fontSize: 12 }}>
                Esta campanha ultrapassou o limiar e está suspensa. A aprovação enfileira o
                disparo com o texto acima: leia antes de aprovar.
              </div>
              <button
                className="btn pri"
                style={{ marginTop: 10 }}
                disabled={!podeAprovar || aprovar.isPending}
                title={podeAprovar ? undefined : 'Apenas titular ou administrador podem aprovar'}
                onClick={() => {
                  if (window.confirm(`Aprovar e disparar "${selecionada.nome}"?`)) {
                    aprovar.mutate(selecionada.id);
                  }
                }}
              >
                {aprovar.isPending ? 'Aprovando...' : 'Aprovar e disparar'}
              </button>
              {!podeAprovar && (
                <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
                  Seu perfil não permite aprovar disparos.
                </p>
              )}
            </>
          )}
        </Drawer>
      )}
    </div>
  );
}
